import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useDispatch } from 'react-redux';

import { styled } from '@mui/material';

import { AuthActions } from 'common/auth/actions';
import { Form } from 'common/component/ValidationFormAndField';
import { CIError } from 'common/error/error_util';
import { CILogger } from 'common/log/log.provider';
import { allowTransientProps } from 'common/utils/styled';
import { TextButton } from 'common/viewcomp/buttons/TextButton';
import { CISecondaryButton } from 'common/viewcomp/forms/buttons';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

import type { SecurityQuestionsFormValues } from './hooks/useSecurityQuestionsForm';
import { useSecurityQuestionsForm } from './hooks/useSecurityQuestionsForm';
import { QuestionAnswerFormField } from './QuestionAnswerFormField';
import { QuestionIndex } from './questionIndexEnum';

interface Props {
    questions: Questions;
    t: TFunction;
    labels?: {
        submit?: string;
    };
    options?: { noAuthUpdate?: boolean; isActivationFlow?: boolean };
    onUpdateSuccess: () => void;
    onCancel?: () => void;
    onBackToLogin?: () => void;
}

interface Questions {
    securityQuestion1Text: string;
    securityQuestion2Text: string;
    securityQuestion3Text: string;
}

export const QuestionAnswerForm = ({
    questions,
    t,
    labels,
    onUpdateSuccess,
    onCancel,
    onBackToLogin,
    options = { noAuthUpdate: false, isActivationFlow: false },
}: Props): ReactElement => {
    const { submitQuestions } = useSecurityQuestionsForm(t);
    const { securityQuestion1Text, securityQuestion2Text, securityQuestion3Text } = questions;
    const { isActivationFlow } = options;
    const dispatch = useDispatch();

    const initialValues: SecurityQuestionsFormValues = {
        questionOne: securityQuestion1Text,
        questionOneAnswer: null,
        questionTwo: securityQuestion2Text,
        questionTwoAnswer: null,
        questionThree: securityQuestion3Text,
        questionThreeAnswer: null,
    };

    const onSubmitQnA = async (values: SecurityQuestionsFormValues): Promise<void> => {
        try {
            await submitQuestions(values);

            if (!options.noAuthUpdate) {
                dispatch(
                    AuthActions.userUpdateSecurityQuestions({
                        securityQuestion1Text: values.questionOne,
                        securityQuestion2Text: values.questionTwo,
                        securityQuestion3Text: values.questionThree,
                    })
                );
            }

            onUpdateSuccess();
        } catch (err) {
            CILogger.getInstance().error(`Error submitting security questions: ${err.message}`);
            CIPageStatus.error(t(CIError.fromGraphQLError(err)?.translationKey));
        }
    };

    return (
        <Form<SecurityQuestionsFormValues>
            initialValues={initialValues}
            onSubmit={onSubmitQnA}
            render={({
                invalid,
                pristine,
                submitting,
                handleSubmit,
                form,
                values: { questionOne, questionTwo, questionThree },
            }): ReactElement => {
                return (
                    <FormStyled $forceWrap={isActivationFlow} onSubmit={handleSubmit} noValidate>
                        <QuestionAnswerFormField
                            questionIndex={QuestionIndex.One}
                            t={t}
                            selectedQuestions={[questionTwo, questionThree]}
                            onChange={() => {
                                form.change('questionOneAnswer', '');
                            }}
                            isActivationFlow={isActivationFlow}
                            labelI18NKey={isActivationFlow ? 'activate.question_one_label' : ''}
                        />
                        <QuestionAnswerFormField
                            questionIndex={QuestionIndex.Two}
                            t={t}
                            selectedQuestions={[questionOne, questionThree]}
                            onChange={() => {
                                form.change('questionTwoAnswer', '');
                            }}
                            isActivationFlow={isActivationFlow}
                            labelI18NKey={isActivationFlow ? 'activate.question_two_label' : ''}
                        />
                        <QuestionAnswerFormField
                            questionIndex={QuestionIndex.Three}
                            t={t}
                            selectedQuestions={[questionTwo, questionOne]}
                            onChange={() => {
                                form.change('questionThreeAnswer', '');
                            }}
                            isActivationFlow={isActivationFlow}
                            labelI18NKey={isActivationFlow ? 'activate.question_three_label' : ''}
                        />

                        {isActivationFlow ? (
                            <ActivationButtonsContainer>
                                <CISecondaryButton
                                    type="submit"
                                    id="edit-question-btn"
                                    isLoading={submitting}
                                    disabled={submitting || invalid || pristine}
                                    style={{ width: '100%', marginTop: '14px' }}
                                    data-testid="activationFormSubmitButton"
                                >
                                    {labels?.submit || t('activate.saveAnswers')}
                                </CISecondaryButton>
                                {onBackToLogin && (
                                    <BackToLoginButton
                                        id="logout-btn"
                                        onClick={onBackToLogin}
                                        data-testid="activationFormLogoutButton"
                                    >
                                        {t('password.back_to_login')}
                                    </BackToLoginButton>
                                )}
                            </ActivationButtonsContainer>
                        ) : (
                            <ButtonsContainer>
                                <TextButton
                                    onClick={onCancel}
                                    id="patient-profile-cancel"
                                    style={{ marginInlineEnd: '20px' }}
                                >
                                    {t('security_questions.cancel')}
                                </TextButton>

                                <CISecondaryButton
                                    type="submit"
                                    id="edit-question-btn"
                                    isLoading={submitting}
                                    disabled={submitting || invalid}
                                >
                                    {t('security_questions.update')}
                                </CISecondaryButton>
                            </ButtonsContainer>
                        )}
                    </FormStyled>
                );
            }}
        />
    );
};

const FormStyled = styled(
    'form',
    allowTransientProps
)<{ $forceWrap: boolean }>(({ theme, $forceWrap }) => ({
    width: '100%',
    '& .MuiGrid-container': {
        flexWrap: $forceWrap ? 'wrap' : 'nowrap',
    },
    '& .MuiGrid-item': {
        width: '100%',
    },

    [theme.breakpoints.down('lg')]: {
        '& .MuiGrid-container': {
            flexWrap: 'wrap',
        },
    },
}));

const ButtonsContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
});

const ActivationButtonsContainer = styled(ButtonsContainer)({
    flexDirection: 'column',
});

const BackToLoginButton = styled(TextButton)({
    marginTop: '15px',
});
